import { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useNavigate } from'react-router-dom';
import { isMobile, isTablet } from "react-device-detect";
import logger from "../utils/logger";

// CSS imports
import './css/DatePickerFix.css'; // Fix for date picker positioning issues

import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, LineElement, PointElement, ArcElement, Title, Tooltip, Legend } from 'chart.js';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboard } from '@fortawesome/free-regular-svg-icons';
import {
    faHouse, faWrench, faRoute, faLocationDot, faBullhorn,
    faBoxesStacked, faTriangleExclamation, faTruck, faClipboardList,
    faFire, faGasPump, faCircleExclamation, faCartShopping,
    faUmbrellaBeach, faClock, faGauge, faBox, faChartBar,
    faFlask, faServer, faFolderOpen
} from '@fortawesome/free-solid-svg-icons';

import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';

// Import Activity Indicator
import { ActivityIndicator } from '../components/custom/ActivityIndicator';

// Import styles sheet
import './css/HomeStyles.css';
import './css/GlobalStyles.css';
import './css/LeftNavigation.css';

import defaultLogo from './img/LogiQOS_logo.png';
import defaultLogoDark from './img/LogiQOS_logo-dark.png';
import truckLoading from './img/truck.gif';

// Providers
import { useTheme } from './themeControl/ThemeProvider';
import { useAuthProvider } from '../providers/AuthProvider';
import { validSession } from '../components/lib/validSession';
import { useAlert } from '../providers/AlertProvider';
import { generateToken } from '../components/lib/FireBaseConfig';

// api
import { use_update_token } from '../api/profiles';
// eslint-disable-next-line
import { fetch_historical_kpis } from '../api/dashboard';
import { get_active_kpis } from '../api/dev';

// Components
import AdvancedCalendar from './custom/Calendar';
import TransportAuditDocuments from './custom/TransportAuditDocuments';
import RouteCalendar from './custom/RouteCalendar';
import DefectsOverlay from './custom/DefectsOverlay';
import EditDefectOverlay from './custom/EditDefectOverlay';
import MonthlyVehicleChecks from './custom/MonthlyVehicleChecks';
import VehicleChecksDisplay from './custom/VehicleCheckDisplay';
import SystemMonitoring from './custom/SystemMonitoring';
import PaletforcePage from './custom/PaletforcePage';
import VehicleTrackers from './custom/VehicleTrackers';
import AlcoholTestingSystem from './custom/AlcoholTestingSystem';
import DashboardDefects from './custom/dashboard/dashboard_defects';
import DashboardFireRegister from './custom/dashboard/dashboard_fireregister';
import DashboardReports from './custom/dashboard/dashboard_reports';
// import AIInsights from './custom/AIInsights';
import UptimeRobotDashboard from './custom/UptimeRobotDashboard';
import TachoManagement from './custom/TachoManagement';
import FuelLogs from './custom/FuelLogs';
import IncidentsManagement from './custom/IncidentsManagement';
import { formatDateToView } from './lib/DateTime';
import Holidays from './custom/Holidays';
import HolidayRequestsModal from './custom/HolidayRequestsModal';

import PurchaseOrders from './custom/report_components/PurchaseOrders';
import TimeSheets from './custom/TimeSheets';
import Collections from './custom/Collections';
import NoticeBoard from './NoticeBoard';

// Refactored Functions
import { HomePageFireRegister } from '../functions/home_page/home_page_fire_register_functions';
import { HomePageVehicles } from '../functions/home_page/home_page_vehicle_functions';
import { HomePageFetchData } from '../functions/home_page/home_page_fetch_functions';
import { HomePageHolidays } from '../functions/home_page/home_page_holidays';
import { DashboardKPIs } from './custom/kpis/DashboardKPIs';


//import OneSignal from 'react-onesignal';
// Firebase
//import { onMessage } from 'firebase/messaging';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    LineElement,
    PointElement,
    ArcElement,
    Title,
    Tooltip,
    Legend
);

const LoggedInHome=()=>{

    const { theme } = useTheme();
    const { logout, isAdmin, isWarehouse, isService, profile, isCustomer, features, isManager, isDev, permissions } = useAuthProvider();
    const { showAlert } = useAlert();
    const navigate = useNavigate();

    // Initialize home page functions (with alerts) - memoized to keep stable references across renders
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // const reportFunctions = useMemo(() => new HomePageReports(showAlert), [showAlert]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fireFunctions = useMemo(() => new HomePageFireRegister(showAlert), [showAlert]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const vehicleFunctions = useMemo(() => new HomePageVehicles(showAlert), [showAlert]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const fetchFunctions = useMemo(() => new HomePageFetchData(showAlert), [showAlert]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const holidaysFunctions = useMemo(() => new HomePageHolidays(showAlert), [showAlert]);

    // const fallbackImage = 'https://via.placeholder.com/150';

    // State to manage active tab
    const [activeTab, setActiveTab] = useState('dashboard');
    
    // State to manage loading state
    const [isLoading, setIsLoading] = useState(false);

    // Show left navigation
    // eslint-disable-next-line
    const [showLeftNav, setShowLeftNav] = useState(false);

    // restricted access for tabs
    const restricted = (isLoading || !isAdmin);
    const superRestricted = (isLoading || (!isManager && !isDev));

    // Auto-redirect SERVICE users to the service tab and lock their company filter
    useEffect(() => {
        if (isService && profile) {
            setActiveTab('service');
            setServiceCalendarCompanyFilter(String(profile.id));
        }
    }, [isService, profile]);

    // States to manage selected profile
    const [selectedDriver, setSelectedDriver] = useState(null);

    // State to manage selected trucks
    const [allUnits, setAllUnits] = useState([]);
    const [allTrailers, setAllTrailers] = useState([]);
    const [allRigids, setAllRigids] = useState([]);
    const [allMHE, setAllMHE] = useState([]);
    const [selectedVehicle, setSelectedVehicle] = useState(null);

    // State to manage selected truck
    const [selectedUnit, setSelectedUnit] = useState(null);
    const [selectedRigid, setSelectedRigid] = useState(null);
    const [selectedTrailer, setSelectedTrailer] = useState(null);
    const [selectedForklift, setSelectedForklift] = useState(null);
    const [selectedDefect, setSelectedDefect] = useState(null);
    
    // State to manage defects
    const [defectsList, setDefectsList] = useState([]);
    const [defectsPassed, setDefectsPassed] = useState([]);
    const [defectsFailed, setDefectsFailed] = useState([]);
    const [defectsRepaired, setDefectsRepaired] = useState([]);
    const [vehiclesWithDefects, setVehiclesWithDefects] = useState([]);

    // State to manage service records
    const [selectedServiceVehicle, setSelectedServiceVehicle] = useState(null);
    const [showServiceRecords, setShowServiceRecords] = useState(false);
    // eslint-disable-next-line
    const [serviceRecords, setAllServiceRecords] = useState([]);
    const [serviceOverdue, setServiceOverdue] = useState([]);
    const [serviceDue, setServiceDue] = useState([]);
    const [serviceCalendarCompanyFilter, setServiceCalendarCompanyFilter] = useState('all');
    
    // State to manage profiles
    const [allProfiles, setAllProfiles] = useState([]);
    const [driversList, setDriversList] = useState([]);
    const [adminList, setAdminList] = useState([]);
    const [serviceList, setServiceList] = useState([]);
    const [warehouseStaff, setWarehouseStaff] = useState([]);
    const serviceCalendarCompanyId = serviceCalendarCompanyFilter === 'all' ? null : serviceList.find(c => String(c.id) === serviceCalendarCompanyFilter)?.id ?? null;
    
    // state to manage defects
    const [defects, setDefects] = useState([]);
    
    // Dashboard states - removed dashboardData state since it's now memoized
    const [showDefects, setShowDefects] = useState(false);
    
    // state to manage outstanding defects
    const [outstandingDefects, setOutstandingDefects] = useState([]);
    const [selectedOutstandingDefects, setSelectedOutstandingDefects] = useState([]);

    // state to manage edit overlay
    const [showEditOverlay, setShowEditOverlay] = useState(false);
    const [lightboxImage, setLightboxImage] = useState(null);

    // monthly checks
    const [monthlyChecksDate, setMonthlyChecksDate] = useState('');
    const [monthlyVehicleChecks, setMonthlyVehicleChecks] = useState([]);
    const [fetchedVehicleId, setFetchedVehicleId] = useState('all');
    const [allVehicles, setAllVehicles] = useState([]);
    
    // Holiday requests state for pending notifications
    const [pendingHolidayRequests, setPendingHolidayRequests] = useState([]);
    const [holidayRequestsCount, setHolidayRequestsCount] = useState(0);
    const [showHolidayRequestsModal, setShowHolidayRequestsModal] = useState(false);
    const [holidaysData, setHolidaysData] = useState([]);

    // state to manage customers
    const [customers, setCustomers] = useState([]);
    const [uniqueCustomers, setUniqueCustomers] = useState([]);
    const [customerDeliveries, setCustomerDeliveries] = useState([]);

    // state to manage fire register
    const [fireRegisterData, setFireRegisterData] = useState([]);
    const [signInMode, setSignInMode] = useState('dropdown'); // 'dropdown' or 'manual'
    const [selectedPerson, setSelectedPerson] = useState('');

    //==================================================================================================================
    // DEBUG ===========================================================================================================
    //==================================================================================================================


    // Reset activeTab to dashboard if current tab's feature is disabled
    useEffect(() => {
        if (!features) return;
        const featureTabMap = {
            collections: features.collections,
            purchaseOrders: features.purchase_orders,
            holidays: features.holidays,
            timeSheets: features.timesheets,
            tacho: features.tacho,
            'alcohol-testing': features.alcohol_testing,
            service: features.service,
            routing: features.routing,
            incidents: features.mobile,
            trackers: (features.velocity === true || features.fleetclear === true || features.fleetview === true) ? true : false,
        };
        const reportsEnabled =
            features.routing !== false ||
            features.service !== false ||
            features.warehouse !== false ||
            features.purchase_orders === true ||
            features.timesheets !== false ||
            features.holidays !== false ||
            features.alcohol_testing !== false ||
            features.tacho !== false ||
            features.fire_register !== false;
        if (activeTab === 'reports' && !reportsEnabled) {
            setActiveTab('dashboard');
        } else if (activeTab !== 'reports' && featureTabMap[activeTab] === false) {
            setActiveTab('dashboard');
        }
    }, [features, activeTab]);

    //==========================================================================================================================
    // FIREBASE ================================================================================================================
    //==========================================================================================================================

    useEffect(() => {
        if (isMobile || isTablet) return;

        const updateToken = async () => {
            if (!profile) return;
            try {
                const token = await generateToken(); // Wait for the token to be resolved
                if (!token) {
                    logger.log("No token generated");
                    return;
                }
    
                if (token) {
                    const { status } = await use_update_token(token, profile.id); // Also await this if it's async
    
                    if (status !== 200 && status !== 201) {
                        showAlert({
                            message: 'Failed to update token',
                            type: 'danger',
                            buttons: [{ label: 'Close' }],
                        });
                        logger.log("Failed to update token");
                    } else {
                        logger.log("Token updated successfully");
                    }
                }
                logger.log("Firebase Token matches database");
            } catch (error) {
                logger.error("Error generating or updating token:", error);
            }
        };
    
        updateToken();
    }, [profile]);

    //------------------------------------------------------------------------
    // const testNotification = async () => {
    //     use_send_notification(profile.firebaseWebToken, "Sending a test notification via the AlertProvider");
    // };
    //------------------------------------------------------------------------
    

    //=========================================================================================================================
    // FETCH DATA =============================================================================================================
    //=========================================================================================================================

    useEffect(() => {
        if (isCustomer) {
            navigate('/customer-portal');
        }
    }, [isCustomer]);


    const fetchData = async () => {
        await fetchFunctions.fetchData( setIsLoading, setDriversList, setAdminList, setServiceList, setWarehouseStaff, setAllProfiles, setDefectsList, setDefectsFailed,
            setDefectsPassed, setDefectsRepaired, setOutstandingDefects, setAllUnits, setAllTrailers, setAllRigids, setAllMHE, setAllVehicles,
            setAllServiceRecords, setServiceOverdue, setServiceDue, setCustomers, setUniqueCustomers, setCustomerDeliveries, logout, navigate, validSession, isService, permissions );
    };

    // Fetch holiday requests count only on component load for performance
    useEffect(() => {
        if (isAdmin && profile && (isDev || permissions.perm_holidays_view)) {
            fetchFunctions.fetchHolidayRequestsCount(setHolidayRequestsCount);
        }
    }, [isAdmin, profile, isDev, permissions]);

    // Service data caching
    const [serviceDataLastFetched, setServiceDataLastFetched] = useState(null);

    // Memoized service data fetching with cache control
    const fetchServiceData = useCallback(async (forceRefresh = false) => {
        const now = Date.now();
        const cacheTimeout = 10 * 60 * 1000; // 10 minutes cache timeout for service data
        
        // Skip fetch if data is recent and not forced refresh
        if (!forceRefresh && serviceDataLastFetched && (now - serviceDataLastFetched) < cacheTimeout) {
            return;
        }
        
        await fetchData();
        setServiceDataLastFetched(now);
    }, [serviceDataLastFetched]);

    // Memoized service data processing
    const processedServiceData = useMemo(() => {
        return {
            serviceRecords,
            serviceOverdue,
            serviceDue,
            allUnits,
            allTrailers,
            allRigids,
            allMHE,
            allVehicles,
            lastUpdated: serviceDataLastFetched ? new Date(serviceDataLastFetched).toLocaleTimeString() : null,
            hasData: Boolean(allVehicles && allVehicles.length > 0)
        };
    }, [serviceRecords, serviceOverdue, serviceDue, allUnits, allTrailers, allRigids, allMHE, allVehicles, serviceDataLastFetched]);

    //=========================================================================================================================
    // USE EFFECTS ============================================================================================================
    //=========================================================================================================================
    
    useEffect(() => {
        if (!profile) return;
        fetchServiceData();
    }, [fetchServiceData, profile]);
    
    // create a list of vehicles that have defects =============================================================================
    useEffect(() => {
        const vehiclesWithDefects = outstandingDefects.map((defect) => defect.vehicle_id);
        setVehiclesWithDefects(vehiclesWithDefects || []);
    }, [outstandingDefects]);


    // Filter outstanding defects based on selected vehicle ====================================================================
    useEffect(() => {
        setIsLoading(true);
        if (!selectedVehicle) {
            setSelectedOutstandingDefects([]);
            setIsLoading(false);
            return;
        }
        const filteredDefects = outstandingDefects.filter((defect) => defect.vehicle_id === selectedVehicle.id);
        setSelectedOutstandingDefects(filteredDefects ? filteredDefects : []);
        setIsLoading(false);
    }, [selectedVehicle, outstandingDefects]);


    // Filter defects based on selected driver ==================================================================================
    useEffect(() => {
        setIsLoading(true);
        if (!selectedDriver) {
            setIsLoading(false);
            return;
        }

        const filteredDefects = defectsList.filter((defect) => defect.driver_id === selectedDriver.id);
        setSelectedDefect(filteredDefects? filteredDefects : []);
        setIsLoading(false);
    }, [selectedDriver]);

    //=========================================================================================================================
    // FUNCTIONS ==============================================================================================================
    //=========================================================================================================================

    const handleChangeTab = (tab) => {

        switch (tab) {
            case 'fireRegister':
                fetchFireRegisterData();
                break;
            case 'dashboard':
                // Use cached data, only refresh if older than 5 minutes
                fetchDashboardData();
                break;
            case 'service':
                // Use cached service data, only refresh if older than 10 minutes
                fetchServiceData();
                break;
            case 'holidays':
                // Fetch full holiday requests data when tab is opened
                if (isAdmin && profile) {
                    fetchFunctions.fetchHolidayRequests(setPendingHolidayRequests, setHolidayRequestsCount);
                }
                break;
            default:
                break;
        }
        
        // set active tab
        setActiveTab(tab)

        // reset fields
        // setShowReport(false);
        setSelectedUnit(null)
        setSelectedRigid(null)
        setSelectedTrailer(null)
        setSelectedForklift(null)
        setShowServiceRecords(null)
        setSelectedServiceVehicle(null);
    };

    const openDefects = (defect_ids) => {
        setDefects(defect_ids);
        setShowDefects(true);
    };

    const closeDefects = () => {
        setDefects([]);
        setShowDefects(false);
        setShowEditOverlay(false);
        setSelectedDefect(null);
    }

    const editDefect = (defect) => {
        setSelectedDefect(defect);
        setShowEditOverlay(true);
    };

    // Update defect ============================================================================================================
    const updateDefect = async (repair) => {
        const profile_uuid = profile.id || '';
        vehicleFunctions.updateDefect( repair, profile_uuid, setIsLoading, closeDefects, fetchData );
    };

    // Fetch monthly vehicle checks =================================================================================================
    const fetchMonthlyChecks = async (date, selected_vehicle_id) => {
        setFetchedVehicleId(selected_vehicle_id || 'all');
        vehicleFunctions.fetchMonthlyChecks( date, selected_vehicle_id, setIsLoading, setMonthlyVehicleChecks );
    };

    // Handle download of monthly vehicle checks =====================================================================
    const handleDownload = async ()=> {
        vehicleFunctions.handleDownloadMonthly(monthlyVehicleChecks, setIsLoading);
    }

    // Handle download of defects only =====================================================================
    const handleDefectsDownload = async () => {
        vehicleFunctions.downloadDefects(monthlyVehicleChecks, setIsLoading);
    };

    // Handle download of monthly report PDF =====================================================================
    const handleMonthlyReportDownload = async (date, selectedVehicleId) => {
        vehicleFunctions.downloadMonthlyReport(date, selectedVehicleId, setIsLoading);
    };

    // Hnadle toggle vehicle in schedule =================================================================================================
    const handleToggleVehicle = async (vehicle_id, type, data) => {
        switch (type) {
            case 'unit':
                setSelectedUnit(data);
                setSelectedRigid(null);
                setSelectedTrailer(null);
                setSelectedForklift(null);
                break;
            case 'rigid':
                setSelectedRigid(data);
                setSelectedUnit(null);
                setSelectedTrailer(null);
                setSelectedForklift(null);
                break;
            case 'trailer':
                setSelectedTrailer(data);
                setSelectedRigid(null);
                setSelectedUnit(null);
                setSelectedForklift(null);
                break;
            case 'forklift':
                setSelectedForklift(data);
                setSelectedUnit(null);
                setSelectedRigid(null);
                setSelectedTrailer(null);
                break;
            default:
                break;
        }

        setSelectedServiceVehicle(vehicle_id)
        setShowServiceRecords(false)
    }

    const handleShowServiceRecords = () => {
        setShowServiceRecords((prev) => !prev);
    };


    // Fire Register Functions ====================================================================================================
    const fetchFireRegisterData = async () => {
        fireFunctions.fetchFireRegisterData( setFireRegisterData );
    };

    const handleSignIn = async (name, surname, company, timeIn, profileId = null) => {
        fireFunctions.handleSignIn( name, surname, company, timeIn, profileId, fetchFireRegisterData );
    };

    const handleSignOut = async (entryId, timeOut) => {
        fireFunctions.handleSignOut( entryId, timeOut, fetchFireRegisterData );
    };

    // Dashboard Functions ====================================================================================================
    const [actualDashboardData, setActualDashboardData] = useState(fetchFunctions.getEmptyDashboardData());
    const [dashboardLastFetched, setDashboardLastFetched] = useState(null);
    const [dashboardLoading, setDashboardLoading] = useState(true); // Add loading state for initial load
    const [activeKpis, setActiveKpis] = useState(null); // null = all visible (fail-open)
    
    // Historical KPI data state
    const [historicalKpis, setHistoricalKpis] = useState([]);
    const [kpiPeriod, setKpiPeriod] = useState(null); // No default selection
    const [kpiLoading, setKpiLoading] = useState(false);
    const [loadingPeriod, setLoadingPeriod] = useState(null); // Track which period is loading

    // Memoized dashboard data fetching with cache control
    const fetchDashboardData = useCallback(async (forceRefresh = false) => {
        const now = Date.now();
        const cacheTimeout = 5 * 60 * 1000; // 5 minutes cache timeout
        
        // Skip fetch if data is recent and not forced refresh
        if (!forceRefresh && dashboardLastFetched && (now - dashboardLastFetched) < cacheTimeout) {
            return;
        }
        
        setDashboardLoading(true);
        try {
            await fetchFunctions.fetchDashboardData(setActualDashboardData);
            setDashboardLastFetched(now);

            // Fetch active KPI visibility settings (fail-open: all shown if request fails)
            try {
                const kpiResp = await get_active_kpis();
                if (kpiResp.status === 200 && kpiResp.data) {
                    setActiveKpis(kpiResp.data);
                }
            } catch {
                // leave activeKpis as null — all KPIs will show
            }
        } catch (error) {
            console.error('Failed to fetch dashboard data:', error);
        } finally {
            setDashboardLoading(false);
        }
    }, [dashboardLastFetched, fetchFunctions]);

    // Fetch Historical KPIs
    const fetchHistoricalKpis = useCallback(async (period = 1) => {
        // Don't fetch if already loading the same period
        if (loadingPeriod === period || kpiPeriod === period) return;
        
        setKpiLoading(true);
        setLoadingPeriod(period);
        
        try {
            const response = await fetch_historical_kpis(period);
            if (response.status === 200 && response.data?.historical_kpis) {
                // Update both data and period together to prevent flicker
                setHistoricalKpis(response.data.historical_kpis);
                setKpiPeriod(period);
            } else {
                console.error('Failed to fetch historical KPIs:', response);
                setHistoricalKpis([]);
            }
        } catch (error) {
            console.error('Error fetching historical KPIs:', error);
            setHistoricalKpis([]);
        } finally {
            setKpiLoading(false);
            setLoadingPeriod(null);
        }
    }, [loadingPeriod, kpiPeriod]);

    // Memoized dashboard data processing
    const processedDashboardData = useMemo(() => {
        if (!actualDashboardData) return fetchFunctions.getEmptyDashboardData();
        
        // Process and optimize data here if needed
        return {
            ...actualDashboardData,
            // Add any computed properties
            hasData: Boolean(actualDashboardData?.defectKpis?.period28Days !== undefined || actualDashboardData?.tachoKpis),
            lastUpdated: dashboardLastFetched ? new Date(dashboardLastFetched).toLocaleTimeString() : null
        };
    }, [actualDashboardData, dashboardLastFetched, fetchFunctions]);

    // Fetch Dashboard Data Effect ============================================================================================
    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);
    
    // Fetch Historical KPIs Effect ===========================================================================================
    useEffect(() => {
        // Only fetch when a period is actually selected
        if (kpiPeriod !== null) {
            fetchHistoricalKpis(kpiPeriod);
        }
    }, [fetchHistoricalKpis, kpiPeriod]);

    // DEBUG=======================================================================================================================


    // DEBUG=======================================================================================================================
    if (!isAdmin && !isWarehouse && !isService) {
        return (
            <div style={{ textAlign: 'center', marginTop: '30vh', height: '100vh', color: 'red' }}>
                <h3>Access Denied</h3>
                <p>You do not have permission to view this page</p>
            </div>
        )
    }

    const isServiceAllowed = isDev || permissions.perm_services_view;
    const isRoutingAllowed = isDev || permissions.perm_routing_view;
    const isNoticeBoardAllowed = isDev || permissions.perm_notice_board_view;
    const isCollectionsAllowed = isDev || permissions.perm_collections_view;
    const isDefectsAllowed = isDev || permissions.perm_defects_view;
    const isIncidentsAllowed = isDev || permissions.perm_incidents_view;
    const isPurchaseOrdersAllowed = isDev || permissions.perm_purchase_orders_view;
    const isHolidaysAllowed = isDev || permissions.perm_holidays_view;
    const isTimesheetsAllowed = isDev || permissions.perm_timesheets_view;
    const isTachoAllowed = isDev || permissions.perm_tacho_view;
    const isPalletforceAllowed = isDev || permissions.perm_palletforce_view;
    const isPallettrackAllowed = isDev || permissions.perm_palletforce_view;
    const isAlcoholAllowed = isDev || permissions.perm_alcohol_view;

    // Dynamic Target Parameters (falls back to hardcoded defaults if not loaded or not defined in activeKpis)
    const targetOperationalRisk = activeKpis?.target_operational_risk !== undefined ? activeKpis.target_operational_risk : 10;
    const targetSubcontractedProfit = activeKpis?.target_subcontracted_profit !== undefined ? activeKpis.target_subcontracted_profit : 30.0;
    const targetWeeklyRevenue = activeKpis?.target_weekly_revenue !== undefined ? activeKpis.target_weekly_revenue : 5.0;
    const targetWeeklyRevenueAmount = activeKpis?.target_weekly_revenue_amount !== undefined ? activeKpis.target_weekly_revenue_amount : 50000.0;
    const targetCollections = activeKpis?.target_collections !== undefined ? activeKpis.target_collections : 350;
    const targetDefectResolution = activeKpis?.target_defect_resolution !== undefined ? activeKpis.target_defect_resolution : 80.0;
    const targetOutstandingDefects = activeKpis?.target_outstanding_defects !== undefined ? activeKpis.target_outstanding_defects : 2;
    const targetTachoCompliance = activeKpis?.target_tacho_compliance !== undefined ? activeKpis.target_tacho_compliance : 0;
    const targetDriverTesting = activeKpis?.target_driver_testing !== undefined ? activeKpis.target_driver_testing : 50.0;
    const targetServiceCompliance = activeKpis?.target_service_compliance !== undefined ? activeKpis.target_service_compliance : 100.0;
    const targetDieselUsage = activeKpis?.target_diesel_usage !== undefined ? activeKpis.target_diesel_usage : 0.40;
    const targetAdblueDieselRatioMin = activeKpis?.target_adblue_diesel_ratio_min !== undefined ? activeKpis.target_adblue_diesel_ratio_min : 3.0;
    const targetAdblueDieselRatioMax = activeKpis?.target_adblue_diesel_ratio_max !== undefined ? activeKpis.target_adblue_diesel_ratio_max : 5.0;

    return (
        <div id={theme} className="container-fluid closed-nav-home-container">
            <Row>
                {/* Left Navigation Panel ==========================================================================================*/}
                <Col className={showLeftNav ? "left-nav" : "closed-nav"} style={{ height: '100vh', paddingBottom: '150px' }} onMouseEnter={() => setShowLeftNav(true)} onMouseLeave={() => setShowLeftNav(false)}>
                    <ul style={{ listStyle: 'none', padding: 0, margin: 0, width: '100%' }}>
                        {!isService && (<li title="Dashboard" className={`nav-item ${activeTab === 'dashboard' ? 'active' : ''}`} style={{ cursor: isLoading ? 'not-allowed' : 'pointer', background: activeTab === 'dashboard' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!isLoading ? () => handleChangeTab('dashboard') : null}><FontAwesomeIcon icon={faHouse} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Dashboard</span></li>)}
                        {features?.service !== false && isServiceAllowed && (<li title="Service Schedule" className={`nav-item ${activeTab === 'service' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'service' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('service')}><FontAwesomeIcon icon={faWrench} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Service Schedule</span></li>)}
                        {!isService && features?.routing !== false && isRoutingAllowed && (<li title="Route Schedule" className={`nav-item ${activeTab === 'routing' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'routing' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('routing')}><FontAwesomeIcon icon={faRoute} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Route Schedule</span></li>)}
                        {!isService && (features?.velocity === true || features?.fleetclear === true || features?.fleetview === true) && isRoutingAllowed && (<li title="Trackers" className={`nav-item ${activeTab === 'trackers' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'trackers' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('trackers')}><FontAwesomeIcon icon={faLocationDot} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Trackers</span></li>)}
                        {!isService && isAdmin && features?.mobile !== false && isNoticeBoardAllowed && (<li title="Notice Board" className={`nav-item ${activeTab === 'noticeBoard' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'noticeBoard' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('noticeBoard')}><FontAwesomeIcon icon={faBullhorn} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Notice Board</span></li>)}
                        {!isService && features?.collections !== false && isCollectionsAllowed && (<li title="Collections" className={`nav-item ${activeTab === 'collections' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'collections' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('collections')}><FontAwesomeIcon icon={faBoxesStacked} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Collections</span></li>)}
                        {!isService && features?.mobile !== false && isDefectsAllowed && (<li title="Drivers Checks" className={`nav-item ${activeTab === 'defects' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'defects' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('defects')}><FontAwesomeIcon icon={faTriangleExclamation} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Drivers Checks</span></li>)}
                        {features?.mobile !== false && isDefectsAllowed && (<li title="Vehicle Defects" className={`nav-item ${activeTab === 'vehicleDefects' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'vehicleDefects' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('vehicleDefects')}><FontAwesomeIcon icon={faTruck} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Vehicle Defects</span></li>)}
                        {!isService && features?.mobile !== false && isDefectsAllowed && (<li title="Monthly Checks" className={`nav-item ${activeTab === 'download' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'download' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('download')}><FontAwesomeIcon icon={faClipboardList} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Monthly Checks</span></li>)}
                        {!isService && features?.fire_register !== false && (<li title="Fire Register" className={`nav-item ${activeTab === 'fireRegister' ? 'active' : ''}`} style={{ cursor: restricted ? 'not-allowed' : 'pointer', background: activeTab === 'fireRegister' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!restricted ? () => handleChangeTab('fireRegister') : null}><FontAwesomeIcon icon={faFire} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Fire Register</span></li>)}
                        {!isService && features?.mobile !== false && isDefectsAllowed && (<li title="Fuel Logs" className={`nav-item ${activeTab === 'fuelLogs' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'fuelLogs' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('fuelLogs')}><FontAwesomeIcon icon={faGasPump} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Fuel Logs</span></li>)}
                        {!isService && features?.mobile !== false && isIncidentsAllowed && (<li title="Accidents / Incidents" className={`nav-item ${activeTab === 'incidents' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'incidents' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('incidents')}><FontAwesomeIcon icon={faCircleExclamation} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Accidents / Incidents</span></li>)}
                        {!isService && features?.purchase_orders !== false && isPurchaseOrdersAllowed && (<li title="Purchase Orders" className={`nav-item ${activeTab === 'purchaseOrders' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'purchaseOrders' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('purchaseOrders')}><FontAwesomeIcon icon={faCartShopping} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Purchase Orders</span></li>)}
                        {!isService && features?.holidays !== false && isHolidaysAllowed && (
                            <li title={`Holidays${isAdmin && holidayRequestsCount > 0 ? ` (${holidayRequestsCount})` : ''}`} className={`nav-item ${activeTab === 'holidays' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'holidays' ? 'rgba(25, 151, 235, 0.8)' : 'transparent', position: 'relative', ...(holidayRequestsCount > 0 && { border: '2px solid #ff6b35' }) }} onClick={() => handleChangeTab('holidays')}>
                                <FontAwesomeIcon icon={faUmbrellaBeach} style={{ width: 16, flexShrink: 0 }} />
                                {isAdmin && holidayRequestsCount > 0 && (<div style={{ position: 'absolute', top: 2, right: 2, background: '#ff6b35', color: 'white', borderRadius: '50%', width: 14, height: 14, fontSize: 9, fontWeight: 'bold', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>{holidayRequestsCount}</div>)}
                                <span className="nav-label">Holidays {isAdmin && holidayRequestsCount > 0 && <span style={{ background: '#ff6b35', color: 'white', borderRadius: '50%', width: 18, height: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 'bold', marginLeft: 4 }}>{holidayRequestsCount}</span>}</span>
                            </li>
                        )}
                        {!isService && features?.timesheets !== false && features?.mobile !== false && isTimesheetsAllowed && (<li title="Clock In/Out" className={`nav-item ${activeTab === 'timeSheets' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'timeSheets' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('timeSheets')}><FontAwesomeIcon icon={faClock} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Clock In/Out</span></li>)}
                        {!isService && features?.tacho !== false && isTachoAllowed && (<li title="Tacho" className={`nav-item ${activeTab === 'tacho' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'tacho' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('tacho')}><FontAwesomeIcon icon={faGauge} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Tacho</span></li>)}
                        {isAdmin && features?.palletforce !== false && isPalletforceAllowed && (<li title="Palletforce" className={`nav-item ${activeTab === 'palletforce' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'palletforce' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('palletforce')}><FontAwesomeIcon icon={faBox} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Palletforce</span></li>)}
                        {isAdmin && features?.pallettrack === true && isPallettrackAllowed && (<li title="PalletTrack" className={`nav-item ${activeTab === 'pallettrack' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'pallettrack' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('pallettrack')}><FontAwesomeIcon icon={faBox} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">PalletTrack</span></li>)}
                        {!isService && (features?.routing !== false || features?.service !== false || features?.warehouse !== false || features?.purchase_orders === true || features?.timesheets !== false || features?.holidays !== false || features?.alcohol_testing !== false || features?.tacho !== false || features?.fire_register !== false) && (<li title="Reports" className={`nav-item ${activeTab === 'reports' ? 'active' : ''}`} style={{ cursor: restricted ? 'not-allowed' : 'pointer', background: activeTab === 'reports' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!restricted ? () => handleChangeTab('reports') : null}><FontAwesomeIcon icon={faChartBar} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Reports</span></li>)}
                        {features?.service !== false && (isDev || permissions.perm_services_edit) && (<li title="Audit Documents" className={`nav-item ${activeTab === 'serviceAudit' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'serviceAudit' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('serviceAudit')}><FontAwesomeIcon icon={faFolderOpen} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Audit Documents</span></li>)}
                        {!isService && features?.alcohol_testing !== false && isAlcoholAllowed && (<li title="Alcohol Testing" className={`nav-item ${activeTab === 'alcohol-testing' ? 'active' : ''}`} style={{ cursor: 'pointer', background: activeTab === 'alcohol-testing' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={() => handleChangeTab('alcohol-testing')}><FontAwesomeIcon icon={faFlask} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">Alcohol Testing</span></li>)}
                        {!isService && features.uptime_robot !== false && (isDev || permissions.perm_system_settings) && (<li title="System Monitoring" className={`nav-item ${activeTab === 'monitoring' ? 'active' : ''}`} style={{ cursor: restricted ? 'not-allowed' : 'pointer', background: activeTab === 'monitoring' ? 'rgba(25, 151, 235, 0.8)' : 'transparent' }} onClick={!restricted ? () => handleChangeTab('monitoring') : null}><FontAwesomeIcon icon={faServer} style={{ width: 16, flexShrink: 0 }} /><span className="nav-label">System Monitoring</span></li>)}
                    </ul>

                    {showLeftNav && (
                        <>
                        {activeTab === 'service' && (
                            <div className="drivers-list" style={{ marginTop: '20px' }}>
                                <div style={{ marginBottom: 12 }}>
                                    {!isService && (
                                    <>
                                    <label style={{ display: 'block', marginBottom: 4, fontSize: 13 }}>Filter by Service Company</label>
                                    <select
                                        value={serviceCalendarCompanyFilter}
                                        onChange={e => setServiceCalendarCompanyFilter(e.target.value)}
                                        style={{ width: '100%', padding: '6px 8px', borderRadius: 4, border: '1px solid #ccc', fontSize: 13 }}
                                    >
                                        <option value="all">All Companies</option>
                                        {serviceList.map(company => (
                                            <option key={company.id} value={company.id}>{company.name}</option>
                                        ))}
                                    </select>
                                    </>
                                    )}
                                </div>
                                <h5><strong>Units</strong></h5>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {processedServiceData.allUnits.filter(v => serviceCalendarCompanyId === null || v.hirer_id === serviceCalendarCompanyId).map((unit) => {
                                        return (
                                            <li
                                                key={unit.id}
                                                style={{
                                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                                    width: '100%',
                                                    padding: '10px',
                                                    borderRadius: 5,
                                                    border: selectedUnit?.id === unit.id ? '1px solid #0588f3' : 'transparent',
                                                    backgroundColor: unit.vor ? null : processedServiceData.serviceOverdue.includes(unit.id)
                                                        ? theme === 'light'
                                                            ? 'rgba(247, 138, 138, 0.8)' // Light theme with defects
                                                            : 'rgba(245, 105, 105, 0.29)' // Dark theme with defects
                                                        : processedServiceData.serviceDue.includes(unit.id)
                                                            ? theme === 'light'
                                                                ? 'rgba(240, 247, 138, 0.80)' // Light theme with defects
                                                                : 'rgba(209, 195, 4, 0.2)' // Dark theme with defects
                                                            : null
                                                }}
                                                onClick={!isLoading ? () => handleToggleVehicle(unit.id, 'unit', unit) : null}
                                            >
                                                <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    {unit.reg}

                                                    {/* Show service records toggle */}
                                                    {selectedServiceVehicle === unit.id && (
                                                        <span
                                                        className='hoverable'
                                                            style={{
                                                                width: 24,
                                                                height: 24,
                                                                display: 'flex',
                                                                alignItems: 'center',
                                                                justifyContent: 'center',
                                                                cursor: 'crosshair',
                                                            }}
                                                            onClick={(event) => {
                                                                event.stopPropagation(); // Prevent parent onClick from triggering
                                                                handleShowServiceRecords();
                                                            }}
                                                        >
                                                            <FontAwesomeIcon icon={faClipboard} style={{ width: 16 }} />
                                                        </span>
                                                    )}
                                                </div>
                                            </li>
                                        )
                                    })}
                                </ul>
                                <h5><strong>Rigids</strong></h5>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {processedServiceData.allRigids.filter(v => serviceCalendarCompanyId === null || v.hirer_id === serviceCalendarCompanyId).map((rigid) => (
                                        <li
                                            key={rigid.id}
                                            style={{
                                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: 5,
                                                border: selectedRigid?.id === rigid.id ? '1px solid #0588f3' : 'transparent',
                                                backgroundColor: rigid.vor ? null : processedServiceData.serviceOverdue.includes(rigid.id)
                                                ? theme === 'light'
                                                    ? 'rgba(247, 138, 138, 0.8)' // Light theme with defects
                                                    : 'rgba(245, 105, 105, 0.29)' // Dark theme with defects
                                                : processedServiceData.serviceDue.includes(rigid.id)
                                                    ? theme === 'light'
                                                        ? 'rgba(240, 247, 138, 0.80)' // Light theme with defects
                                                        : 'rgba(209, 195, 4, 0.2)' // Dark theme with defects
                                                    : null
                                            }}
                                            onClick={!isLoading ? () => handleToggleVehicle(rigid.id, 'rigid', rigid) : null}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                                {rigid.reg}

                                                {/* Show service records toggle */}
                                                {selectedServiceVehicle === rigid.id && (
                                                    <span
                                                        style={{
                                                            width: 24,
                                                            height: 24,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'crosshair',
                                                        }}
                                                        onClick={(event) => {
                                                            event.stopPropagation(); // Prevent parent onClick from triggering
                                                            handleShowServiceRecords();
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faClipboard} style={{ width: 16 }} />
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                
                                <h5><strong>Trailers</strong></h5>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {processedServiceData.allTrailers.filter(v => serviceCalendarCompanyId === null || v.hirer_id === serviceCalendarCompanyId).map((trailer) => (
                                        <li
                                            key={trailer.id}
                                            style={{
                                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: 5,
                                                border: selectedTrailer?.id === trailer.id ? '1px solid #0588f3' : 'transparent',
                                                backgroundColor: trailer.vor ? null : processedServiceData.serviceOverdue.includes(trailer.id)
                                                ? theme === 'light'
                                                    ? 'rgba(247, 138, 138, 0.8)' // Light theme with defects
                                                    : 'rgba(245, 105, 105, 0.29)' // Dark theme with defects
                                                : processedServiceData.serviceDue.includes(trailer.id)
                                                    ? theme === 'light'
                                                        ? 'rgba(240, 247, 138, 0.80)' // Light theme with defects
                                                        : 'rgba(209, 195, 4, 0.2)' // Dark theme with defects
                                                    : null
                                            }}
                                            onClick={!isLoading ? () => handleToggleVehicle(trailer.id, 'trailer', trailer) : null}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                                {trailer.reg}

                                                {/* Show service records toggle */}
                                                {selectedServiceVehicle === trailer.id && (
                                                    <span
                                                        style={{
                                                            width: 24,
                                                            height: 24,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'crosshair',
                                                        }}
                                                        onClick={(event) => {
                                                            event.stopPropagation(); // Prevent parent onClick from triggering
                                                            handleShowServiceRecords();
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faClipboard} style={{ width: 16 }} />
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                                <h5><strong>MHE</strong></h5>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {processedServiceData.allMHE.filter(v => serviceCalendarCompanyId === null || v.hirer_id === serviceCalendarCompanyId).map((forklift) => (
                                        <li
                                            key={forklift.id}
                                            style={{ cursor: isLoading ? 'not-allowed' : 'pointer', width: '100%', padding: '10px', borderRadius: 5, border: selectedForklift?.id === forklift.id ? '1px solid #0588f3' : 'transparent', backgroundColor: forklift.vor ? null : processedServiceData.serviceOverdue.includes(forklift.id) ? (theme === 'light' ? 'rgba(247, 138, 138, 0.8)' : 'rgba(245, 105, 105, 0.29)') : processedServiceData.serviceDue.includes(forklift.id) ? (theme === 'light' ? 'rgba(240, 247, 138, 0.80)' : 'rgba(209, 195, 4, 0.2)') : null }}
                                            onClick={!isLoading ? () => handleToggleVehicle(forklift.id, 'forklift', forklift) : null}
                                        >
                                            <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                                                {forklift.reg}

                                                {/* Show service records toggle */}
                                                {selectedServiceVehicle === forklift.id && (
                                                    <span
                                                        style={{
                                                            width: 24,
                                                            height: 24,
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'center',
                                                            cursor: 'crosshair',
                                                        }}
                                                        onClick={(event) => {
                                                            event.stopPropagation(); // Prevent parent onClick from triggering
                                                            handleShowServiceRecords();
                                                        }}
                                                    >
                                                        <FontAwesomeIcon icon={faClipboard} style={{ width: 16 }} />
                                                    </span>
                                                )}
                                            </div>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}


                        {activeTab === 'defects' && (
                            <div className="drivers-list" style={{ marginTop: '20px' }}>
                                <h5><strong>Drivers</strong></h5>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {driversList.map((driver) => (
                                        <li
                                            key={driver.id}
                                            style={{
                                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: 5,
                                                marginBottom: '5px',
                                                border: selectedDriver?.id === driver.id ? '1px solid #0588f3' : 'transparent',
                                                backgroundColor: defectsPassed.includes(driver.id)
                                                ? theme === 'light' 
                                                    ? 'rgba(174, 247, 138, 0.7)' 
                                                    : 'rgba(174, 247, 138, 0.2)'
                                                : defectsFailed.includes(driver.id)
                                                    ? theme === 'light'
                                                        ? 'rgba(247, 138, 138, 0.7)'
                                                        : 'rgba(247, 138, 138, 0.2)'
                                                : defectsRepaired.includes(driver.id)
                                                    ? theme === 'light'
                                                        ? 'rgba(174, 247, 138, 0.7)'
                                                        : 'rgba(174, 247, 138, 0.2)'
                                                    : 'transparent'                                        
                                                }}
                                            onClick={!isLoading ? () => setSelectedDriver(driver) : null}
                                        >
                                            {driver.name} {driver.surname}
                                        </li>
                                    ))}
                                </ul>

                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    <h5><strong>Admin</strong></h5>
                                    {adminList.map((admin) => (
                                        <li
                                            key={admin.id}
                                            style={{
                                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: 5,
                                                marginBottom: '5px',
                                                border: selectedDriver?.id === admin.id ? '1px solid #0588f3' : 'transparent',
                                                backgroundColor: defectsPassed.includes(admin.id)
                                                ? theme === 'light' 
                                                    ? 'rgba(174, 247, 138, 0.7)' 
                                                    : 'rgba(174, 247, 138, 0.2)'
                                                : defectsFailed.includes(admin.id)
                                                    ? theme === 'light'
                                                        ? 'rgba(247, 138, 138, 0.7)'
                                                        : 'rgba(247, 138, 138, 0.2)'
                                                : defectsRepaired.includes(admin.id)
                                                    ? theme === 'light'
                                                        ? 'rgba(174, 247, 138, 0.7)'
                                                        : 'rgba(174, 247, 138, 0.2)'
                                                    : 'transparent'
                                            }}
                                            onClick={!isLoading ? () => setSelectedDriver(admin) : null}
                                        >
                                            {admin.name} {admin.surname}
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        )}


                        {activeTab === 'vehicleDefects' && (
                            <div className="drivers-list" style={{ marginTop: '20px' }}>
                                <h5><strong>Units</strong></h5>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {processedServiceData.allUnits.map((unit) => (
                                        <li
                                            key={unit.id}
                                            style={{
                                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: 5,
                                                marginBottom: '5px',
                                                border: selectedVehicle?.id === unit.id ? '1px solid #0588f3' : 'transparent',
                                                backgroundColor: !vehiclesWithDefects.includes(unit.id)
                                                    ? theme === 'light'
                                                        ? 'rgba(174, 247, 138, 0.7)' // Light theme with defects
                                                        : 'rgba(174, 247, 138, 0.2)' // Dark theme with defects
                                                    : theme === 'light'
                                                        ? 'rgba(247, 138, 138, 0.7)' // Light theme without defects
                                                        : 'rgba(247, 138, 138, 0.2)' // Dark theme without defects
                                            }}
                                            
                                                onClick={!isLoading ? () => setSelectedVehicle(unit) : null}
                                            >
                                            {unit.reg}
                                        </li>
                                    ))}
                                </ul>

                                <h5><strong>Trailers</strong></h5>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {processedServiceData.allTrailers.map((unit) => (
                                        <li
                                            key={unit.id}
                                            style={{
                                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: 5,
                                                marginBottom: '5px',
                                                border: selectedVehicle?.id === unit.id ? '1px solid #0588f3' : 'transparent',
                                                backgroundColor: !vehiclesWithDefects.includes(unit.id)
                                                    ? theme === 'light'
                                                        ? 'rgba(174, 247, 138, 0.7)' // Light theme with defects
                                                        : 'rgba(174, 247, 138, 0.2)' // Dark theme with defects
                                                    : theme === 'light'
                                                        ? 'rgba(247, 138, 138, 0.7)' // Light theme without defects
                                                        : 'rgba(247, 138, 138, 0.2)' // Dark theme without defects
                                            }}
                                            
                                                onClick={!isLoading ? () => setSelectedVehicle(unit) : null}
                                            >
                                            {unit.reg}
                                        </li>
                                    ))}
                                </ul>

                                <h5><strong>Rigids</strong></h5>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {processedServiceData.allRigids.map((unit) => (
                                        <li
                                            key={unit.id}
                                            style={{
                                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: 5,
                                                marginBottom: '5px',
                                                border: selectedVehicle?.id === unit.id ? '1px solid #0588f3' : 'transparent',
                                                backgroundColor: !vehiclesWithDefects.includes(unit.id)
                                                    ? theme === 'light'
                                                        ? 'rgba(174, 247, 138, 0.7)' // Light theme with defects
                                                        : 'rgba(174, 247, 138, 0.2)' // Dark theme with defects
                                                    : theme === 'light'
                                                        ? 'rgba(247, 138, 138, 0.7)' // Light theme without defects
                                                        : 'rgba(247, 138, 138, 0.2)' // Dark theme without defects
                                            }}
                                            
                                                onClick={!isLoading ? () => setSelectedVehicle(unit) : null}
                                            >
                                            {unit.reg}
                                        </li>
                                    ))}
                                </ul>

                                <h5><strong>MHE</strong></h5>
                                <ul style={{ listStyle: 'none', padding: 0 }}>
                                    {processedServiceData.allMHE.map((unit) => (
                                        <li
                                            key={unit.id}
                                            style={{
                                                cursor: isLoading ? 'not-allowed' : 'pointer',
                                                width: '100%',
                                                padding: '10px',
                                                borderRadius: 5,
                                                marginBottom: '5px',
                                                border: selectedVehicle?.id === unit.id ? '1px solid #0588f3' : 'transparent',
                                                backgroundColor: !vehiclesWithDefects.includes(unit.id)
                                                    ? theme === 'light'
                                                        ? 'rgba(174, 247, 138, 0.7)' // Light theme with defects
                                                        : 'rgba(174, 247, 138, 0.2)' // Dark theme with defects
                                                    : theme === 'light'
                                                        ? 'rgba(247, 138, 138, 0.7)' // Light theme without defects
                                                        : 'rgba(247, 138, 138, 0.2)' // Dark theme without defects
                                            }}
                                            
                                                onClick={!isLoading ? () => setSelectedVehicle(unit) : null}
                                            >
                                            {unit.reg}
                                        </li>
                                    ))}
                                </ul>

                                {vehiclesWithDefects.includes(1) && (
                                    <>
                                        <h5><strong>External Trailers</strong></h5>
                                        <ul style={{ listStyle: 'none', padding: 0 }}>
                                            <li
                                                key={1}
                                                style={{
                                                    cursor: isLoading ? 'not-allowed' : 'pointer',
                                                    width: '100%',
                                                    padding: '10px',
                                                    borderRadius: 5,
                                                    marginBottom: '5px',
                                                    border: selectedVehicle?.id === 1 ? '1px solid #0588f3' : 'transparent',
                                                    backgroundColor: !vehiclesWithDefects.includes(1)
                                                        ? theme === 'light'
                                                            ? 'rgba(174, 247, 138, 0.7)'
                                                            : 'rgba(174, 247, 138, 0.2)'
                                                        : theme === 'light'
                                                            ? 'rgba(247, 138, 138, 0.7)'
                                                            : 'rgba(247, 138, 138, 0.2)'
                                                }}
                                                onClick={!isLoading ? () => setSelectedVehicle({ id: 1, reg: 'External Trailer' }) : null}
                                            >
                                                External Trailer
                                            </li>
                                        </ul>
                                    </>
                                )}
                            </div>
                        )}
                    </>
                )}
                </Col>

                {/* Right Content Panel ============================================================================================================*/}
                <Col className="closed-nav-content" style={{ padding: '20px' }}>
                {isLoading ? (
                            <div style={{ alignSelf: 'center', justifyContent: 'center', marginTop: '30vh' }}>
                                <ActivityIndicator />
                            </div>
                    ) : (
                    <>
                    
                    {/* Dashboard Content */}
                    {activeTab === 'dashboard' && (
                        <div style={{ 
                            minHeight: '100vh',
                            background: `linear-gradient(135deg, rgba(25, 151, 235, 0.1) 0%, rgba(25, 151, 235, 0.05) 100%)`,
                            padding: '20px',
                            borderRadius: '10px'
                        }}>
                            {!isAdmin ? (
                                <p style={{ color: 'red', fontWeight: 'bold', textAlign: 'center', fontSize: '22px' }}>Admin Access Required</p>
                            ) : (
                                <>
                                    {!(features?.routing === false && features?.warehouse === false && features?.kpis === false) && (
                                    <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                                        <h2 style={{ color: theme === 'light' ? '#333' : '#fff', marginBottom: '10px', width: '100%', fontSize: '28px', fontWeight: '600' }}>
                                            📊 Business Intelligence Dashboard
                                        </h2>
                                        <p style={{ color: theme === 'light' ? '#666' : '#ccc', fontSize: '16px', maxWidth: '600px', margin: '0 auto' }}>
                                            Real-time operational KPIs and performance metrics for strategic fleet management
                                            {processedDashboardData.lastUpdated && (
                                                <span style={{ fontSize: '12px', display: 'block', marginTop: '8px', color: theme === 'light' ? '#999' : '#888' }}>
                                                    📈 Last updated: {processedDashboardData.lastUpdated}
                                                </span>
                                            )}
                                        </p>
                                    </div>
                                    )}

                            {features?.kpis !== false && (dashboardLoading ? (
                                <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '400px', marginBottom: '40px', background: theme === 'light' ? '#fff' : '#333', borderRadius: '15px', boxShadow: '0 8px 16px rgba(0,0,0,0.1)', border: '1px solid ' + (theme === 'light' ? '#e0e0e0' : '#555'), flexDirection: 'column', gap: '20px' }}>
                                    <img src={truckLoading} alt="Loading..." 
                                        style={{ 
                                            width: '80px', 
                                            height: '80px',
                                            opacity: 0.7 
                                        }} 
                                    />
                                    <div style={{ fontSize: '16px', color: theme === 'light' ? '#666' : '#ccc', fontWeight: '500' }}>
                                        Loading KPI Dashboard...
                                    </div>
                                </div>
                            ) : (
                                <DashboardKPIs
                                    processedDashboardData={processedDashboardData}
                                    activeKpis={activeKpis}
                                    features={features}
                                    theme={theme}

                                    targetOperationalRisk={targetOperationalRisk}
                                    targetSubcontractedProfit={targetSubcontractedProfit}
                                    targetWeeklyRevenue={targetWeeklyRevenue}
                                    targetCollections={targetCollections}
                                    targetDefectResolution={targetDefectResolution}
                                    targetTachoCompliance={targetTachoCompliance}
                                    targetDriverTesting={targetDriverTesting}
                                    targetServiceCompliance={targetServiceCompliance}
                                    targetDieselUsage={targetDieselUsage}
                                    targetOutstandingDefects={targetOutstandingDefects}
                                    targetAdblueDieselRatioMin={targetAdblueDieselRatioMin}
                                    targetAdblueDieselRatioMax={targetAdblueDieselRatioMax}

                                    superRestricted={superRestricted}
                                    isCollectionsAllowed={isCollectionsAllowed}
                                    isDefectsAllowed={isDefectsAllowed}
                                    isTachoAllowed={isTachoAllowed}
                                    isAlcoholAllowed={isAlcoholAllowed}
                                    isServiceAllowed={isServiceAllowed}

                                    handleChangeTab={handleChangeTab}

                                    historicalKpis={historicalKpis}
                                    kpiPeriod={kpiPeriod}
                                    kpiLoading={kpiLoading}
                                    loadingPeriod={loadingPeriod}
                                    fetchHistoricalKpis={fetchHistoricalKpis}
                                    targetWeeklyRevenueAmount={targetWeeklyRevenueAmount}
                                />
                            ))}
                            </>
                            )}
                        </div>
                    )}

                    {/* Right Content Service */}
                    {activeTab === 'service' && (
                        <>
                        <span style={{ textAlign: 'center'}}>
                            <h3>Service Schedule</h3>
                        </span>

                            <AdvancedCalendar
                                theme={theme}
                                selectedVehicle={selectedServiceVehicle}
                                allVehicles={processedServiceData.allVehicles}
                                serviceCompanies={serviceList}
                                showServiceRecords={showServiceRecords}
                                filterCompanyId={serviceCalendarCompanyId}
                            />
                        </>
                    )}

                    {activeTab === 'serviceAudit' && (isDev || permissions.perm_services_edit) && (
                        <TransportAuditDocuments theme={theme} />
                    )}

                    {/* Right Content Routing */}
                    {activeTab === 'routing' && (
                        <>
                        <span style={{ textAlign: 'center'}}>
                            <h3>Route Schedule</h3>
                        </span>

                            <RouteCalendar
                                theme={theme}
                                drivers={[...driversList, ...adminList]}
                                vehicles={processedServiceData.allVehicles}
                            />
                        </>
                    )}

                    {/* Trackers — live vehicle positions */}
                    {activeTab === 'trackers' && (features?.velocity === true || features?.fleetclear === true || features?.fleetview === true) && (
                        <div style={{ padding: '0 4px' }}>
                            <span style={{ textAlign: 'center', display: 'block' }}>
                                <h3>Live Vehicle Trackers</h3>
                            </span>
                            <VehicleTrackers
                                theme={theme}
                                vehicles={processedServiceData.allVehicles}
                                features={features}
                            />
                        </div>
                    )}

                    {/* Right Content Collections */}
                    {activeTab === 'collections' && isCollectionsAllowed && (
                        <Collections
                            customers={customers}
                            profile={profile}
                        />
                    )}

                    {/* Right Content Defects */}
                    {activeTab === 'defects' && (
                        <DashboardDefects
                            selectedDefect={selectedDefect}
                            selectedDriver={selectedDriver}
                            openDefects={openDefects}
                            theme={theme}
                        />
                    )}

                    {/* show defects component */}
                    {showDefects && (
                        <DefectsOverlay
                            outstanding={outstandingDefects}
                            defects={defects}
                            setShowDefects={setShowDefects}
                            updateDefect={updateDefect}
                            theme={theme}
                        />
                    )}

                    {activeTab === 'vehicleDefects' && (
                        <>
                            <div style={{ textAlign: 'center' }}>
                                <h3>Vehicle Defects</h3>
                            </div>
                            {selectedVehicle && selectedOutstandingDefects.length > 0 ? (
                                selectedOutstandingDefects.map((defect) => (
                                    <div
                                        className="defect-container"
                                        key={defect.id}
                                        style={{
                                            flexDirection: 'row',
                                            margin: '20px auto',
                                            maxWidth: 800,
                                            width: '100%',
                                        }}
                                    >
                                        <div style={{ flexDirection: 'column', width: '30%' }}>
                                            <h5><strong>Defect Details</strong></h5>

                                            {/* Always show date, vehicle_reg, and duration */}
                                            {['driver_name', 'created_at', 'defect_type', 'status', 'comments'].map((key) => {
                                                const value = defect[key];
                                                if (!value && key !== 'defect_type') return null; // Skip if value is missing (except defect_type)
                                                const formattedKey = key.replace(/_/g, ' ').charAt(0).toUpperCase() + key.replace(/_/g, ' ').slice(1);
                                                return (
                                                    <p
                                                        key={key}
                                                        style={{
                                                            marginBottom: '0px',
                                                            marginLeft: '5px',
                                                            padding: '5px',
                                                        }}
                                                    >
                                                        <strong>{formattedKey}:</strong> {key === 'created_at' ? formatDateToView(value.split("T")[0]) : value}
                                                    </p>
                                                );
                                            })}

                                            {/* Display button at the bottom */}
                                            <div className="show-defects-button">
                                                <button
                                                    className="btn btn-primary"
                                                    style={{
                                                        marginTop: '10px',
                                                        padding: '5px',
                                                        borderRadius: '5px',
                                                    }}
                                                    title="View Defects"
                                                    onClick={() =>  editDefect(defect)}
                                                >
                                                    Edit Defect
                                                </button>
                                            </div>
                                        </div>

                                        {/* Display image */}
                                        {defect.defect_image && (
                                            <img
                                                src={defect.defect_image}
                                                alt='Defect'
                                                style={{
                                                    width: '20%',
                                                    borderRadius: '5px',
                                                    objectFit: 'cover',
                                                    cursor: 'pointer',
                                                }}
                                                onClick={() => setLightboxImage(defect.defect_image)}
                                            />
                                        )}
                                    </div>
                                ))
                            ) : (
                                <div style={{ textAlign: 'center' }}>
                                    {selectedVehicle ? (
                                        <p>No defects found!</p>
                                    ) : (
                                        <p>Please select a vehicle from the list</p>
                                    )}
                                </div>
                            )}
                        </>
                    )}
                    
                    {/* Defect Image Lightbox */}
                    {lightboxImage && (
                        <div
                            role="dialog"
                            aria-label="Defect image viewer"
                            style={{
                                position: 'fixed',
                                top: 0,
                                left: 0,
                                width: '100vw',
                                height: '100vh',
                                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                zIndex: 2000,
                                cursor: 'zoom-out',
                            }}
                            onClick={() => setLightboxImage(null)}
                        >
                            <img
                                src={lightboxImage}
                                alt="Defect full size"
                                style={{
                                    maxWidth: '90vw',
                                    maxHeight: '90vh',
                                    borderRadius: '8px',
                                    objectFit: 'contain',
                                    boxShadow: '0 4px 32px rgba(0,0,0,0.6)',
                                }}
                                onClick={(e) => e.stopPropagation()}
                            />
                            <button
                                onClick={() => setLightboxImage(null)}
                                style={{
                                    position: 'absolute',
                                    top: '20px',
                                    right: '24px',
                                    background: 'rgba(0,0,0,0.5)',
                                    border: 'none',
                                    color: '#fff',
                                    fontSize: '28px',
                                    lineHeight: 1,
                                    borderRadius: '50%',
                                    width: '40px',
                                    height: '40px',
                                    cursor: 'pointer',
                                }}
                                aria-label="Close image viewer"
                            >
                                &times;
                            </button>
                        </div>
                    )}

                    {/* Edit Defect Overlay */}
                    {showEditOverlay && (
                        <EditDefectOverlay
                            defect={selectedDefect}
                            setShowDefects={setShowEditOverlay}
                            updateDefect={updateDefect}
                            theme="dark"
                        />
                    )}

                    {/* Download Monthly Vehicle Checks */}
                    {activeTab === 'download' && (
                        <>
                            <div style={{ textAlign: 'center' }}>
                                <h3>Monthly Vehicle Checks</h3>
                                <MonthlyVehicleChecks
                                    setMonthlyChecks={setMonthlyChecksDate}
                                    allVehicles={allVehicles}
                                    fetchChecks={fetchMonthlyChecks}
                                    downloadData={handleDownload}
                                    downloadDefects={handleDefectsDownload}
                                    downloadReport={handleMonthlyReportDownload}
                                    hasData={monthlyVehicleChecks && monthlyVehicleChecks.length > 0}
                                    fetchedDate={monthlyChecksDate}
                                    fetchedVehicleId={fetchedVehicleId}
                                />
                                {monthlyChecksDate && (
                                    <VehicleChecksDisplay checks={monthlyVehicleChecks} theme={theme} />
                                )}
                            </div>
                        </>
                    )}

                    {/* Fire Register */}
                    {activeTab === 'fireRegister' && (
                        <DashboardFireRegister
                            theme={theme}
                            fireRegisterData={fireRegisterData}
                            fetchFireRegisterData={fetchFireRegisterData}
                            handleSignIn={handleSignIn}
                            handleSignOut={handleSignOut}
                            adminList={adminList}
                            driversList={driversList}
                            signInMode={signInMode}
                            setSignInMode={setSignInMode}
                            selectedPerson={selectedPerson}
                            setSelectedPerson={setSelectedPerson}
                            showAlert={showAlert}
                        />
                    )}

                    {/* Purchase Orders */}
                    {activeTab === 'purchaseOrders' && (
                        <PurchaseOrders theme={theme} />
                    )}

                    {/* Holidays */}
                    {activeTab === 'holidays' && (
                        <Holidays 
                            theme={theme} 
                            drivers={driversList}
                            admins={adminList}
                            warehouseStaff={warehouseStaff}
                            pendingHolidayRequests={pendingHolidayRequests}
                            holidayRequestsCount={holidayRequestsCount}
                            showHolidayRequestsModal={showHolidayRequestsModal}
                            setShowHolidayRequestsModal={setShowHolidayRequestsModal}
                            holidaysData={holidaysData}
                            setHolidaysData={setHolidaysData}
                        />
                    )}

                    {/* Time Sheets */}
                    {activeTab === 'timeSheets' && (
                        <TimeSheets theme={theme} />
                    )}

                    {/* Reports */}
                    {console.log("DashboardReports reference:",DashboardReports)}
                    {activeTab === 'reports' && (
                        <DashboardReports
                            showAlert={showAlert}
                            customers={customers}
                            customerDeliveries={customerDeliveries}
                            uniqueCustomers={uniqueCustomers}
                            driversList={driversList}
                            adminList={adminList}
                            warehouseStaff={warehouseStaff}
                            allTrailers={allTrailers}
                            features={features}
                            permissions={permissions}
                            isDev={isDev}
                            isRoutingAllowed={isRoutingAllowed}
                            isPurchaseOrdersAllowed={isPurchaseOrdersAllowed}
                            isTimesheetsAllowed={isTimesheetsAllowed}
                            isHolidaysAllowed={isHolidaysAllowed}
                            isAlcoholAllowed={isAlcoholAllowed}
                            isTachoAllowed={isTachoAllowed}
                            holidaysFunctions={holidaysFunctions}
                            theme={theme}
                        />
                    )}

                    {/* Alcohol Testing System */}
                    {activeTab === 'alcohol-testing' && (
                        <div style={{ padding: '0' }}>
                            <AlcoholTestingSystem theme={theme} />
                        </div>
                    )}



                    {/* System Monitoring */}
                    {activeTab === 'monitoring' && (isDev || permissions.perm_system_settings) && (
                        <div style={{ padding: '0' }}>
                            <SystemMonitoring theme={theme} />
                            {features.uptime_robot !== false && (
                                <div style={{ marginTop: '2rem' }}>
                                    <UptimeRobotDashboard theme={theme} />
                                </div>
                            )}
                        </div>
                    )}

                    {/* Fuel Logs */}
                    {activeTab === 'fuelLogs' && (
                        <div style={{ padding: '0' }}>
                            <FuelLogs theme={theme} isManagement={isManager} />
                        </div>
                    )}

                    {/* Accidents / Incidents */}
                    {activeTab === 'incidents' && (
                        <div style={{ padding: '0' }}>
                            <IncidentsManagement theme={theme} showAlert={showAlert} allProfiles={allProfiles} />
                        </div>
                    )}

                    {/* Notice Board */}
                    {activeTab === 'noticeBoard' && (
                        <NoticeBoard theme={theme} isAdmin={isAdmin} />
                    )}

                    {/* Tacho Management */}
                    {activeTab === 'tacho' && (
                        <div style={{ padding: '0' }}>
                            <TachoManagement 
                                theme={theme}
                                isLoading={isLoading}
                                isAdmin={isAdmin}
                                profile={profile}
                            />
                        </div>
                    )}

                    {/* Palletforce */}
                    {activeTab === 'palletforce' && isAdmin && (
                        <div style={{ padding: '0' }}>
                            <PaletforcePage
                                theme={theme}
                                showAlert={showAlert}
                                integrationId="b303ab67-e80c-4497-8bcb-a3cd1ecaa988"
                            />
                        </div>
                    )}

                    {/* PalletTrack */}
                    {activeTab === 'pallettrack' && isAdmin && features?.pallettrack === true && (
                        <div style={{ padding: '0' }}>
                            <PaletforcePage
                                theme={theme}
                                showAlert={showAlert}
                                provider="pallettrack"
                            />
                        </div>
                    )}

                    </>
                    )}
                </Col>
            </Row>
            
            {/* Holiday Requests Modal */}
            <HolidayRequestsModal
                theme={theme}
                show={showHolidayRequestsModal}
                onHide={() => setShowHolidayRequestsModal(false)}
                pendingRequests={pendingHolidayRequests}
                holidaysData={holidaysData}
                setHolidaysData={setHolidaysData}
                onRequestsUpdated={() => {
                    // Refresh count and full data when requests are processed
                    fetchFunctions.fetchHolidayRequests(setPendingHolidayRequests, setHolidayRequestsCount);
                }}
            />
        </div>
    );
}

const LoggedOutHome=()=>{
    const { theme } = useTheme();
    //show default logo only outside of the login page
    const logoSrc = theme === 'dark' ? defaultLogoDark : defaultLogo;
    
    if (isMobile || isTablet) {
        return (
            <div>
                <span style={{ color: 'red' }}>This app is only available on desktop.</span>
            </div>
        )    
    }

    return(
        <div id={theme} className="login-container">
            {/* Fullscreen Background */}
            <div className={theme === 'dark' ? "dark-fullscreen-background" : "fullscreen-background"}></div>

            {/* Login */}
            <div style={{
                position: 'absolute',  // Makes the element positionable on the page
                top: '120px',          // Starts the div 200px from the top
                left: 0,              // Aligns the div to the right edge
                width: '500px',         // Let the width auto adjust
                height: '90vh',       // Sets the height to 200% of the viewport height
                zIndex: 10,
                padding: 50,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',  // Centers content vertically within the box
                alignItems: 'center',      // Centers content horizontally within the box
                backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.9)' : 'rgba(168, 165, 165, 0.9)',
            }}>
            <img src={logoSrc} alt="logo" width={300} style={{ paddingBottom: '100px' }}/>
                <Link className="btn btn-primary btn-lg" styles={{ width: 300 }}to='/login'>Login</Link>
            </div>    
        </div>
    )
}


const HomePage=()=>{
    const { loggedIn } = useAuthProvider();
    return(
        <>
            {loggedIn?<LoggedInHome/>:<LoggedOutHome/>}
        </>
    )
}

export default HomePage;